import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  DONT_NOTIFY,
  NOTIFICATIONS_CTA_ICON,
  NOTIFICATIONS_CTA_PRIMARY_TRANSLATIONS,
  NOTIFICATIONS_CTA_SECONDARY_TRANSLATIONS,
  NOTIFICATIONS_CTA_TRANSLATIONS,
  NOTIFICATIONS_TRANSLATIONS,
  NotificationGroup,
  NotificationsStatus,
  NotificationType,
  NOTIFICATION_GROUP,
  NOTIFICATION_STATUS,
  NOTIFICATIONS_LIMIT,
  NOTIFICATIONS_NUMBER_OF_PLACEHOLDERS,
} from 'models/Notification';
import NotificationsContext, {
  NotificationsCountType,
} from './Notifications.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { getNotificationRedirectionPath } from './redirections';
import useInfinitePagination from 'hooks/useInfinitePagination';
import AuthContext from 'providers/Auth/Auth.context';
import { useLocation, useNavigate } from 'react-router-dom';
import notify from 'modules/notify';
import api from 'api';
import utils, {
  getIsNotificationUnread,
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
  sentryError,
} from 'utils';
import { DEFAULT_ANIMATION_TIME_MS } from 'components/PopNotificationCard/PopNotificationCard.component';
import { getNotificationRedirectionDynamicPath } from './redirections-dynamic';
import { useTranslation } from 'react-i18next';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { getNudgesString } from './utils';
import useQueryParams from 'hooks/useQueryParams';
import useAction from './hooks/useAction';
import useLoadingState from 'hooks/useLoadingState';
import mixpanelService from 'services/mixpanelService';
import { NotificationViewedInitiatedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';

const DATA_ROOM = 'data-room';

const getFileNames = (fileNames: string[] = []) =>
  getJoinedWordsWithComma(fileNames);

const Notifications: React.FC = (props) => {
  const { children } = props;

  const { isLoggedIn } = useContext(AuthContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [notificationsCount, setNotificationsCount] =
    useState<NotificationsCountType>();
  const [notificationStatus, setNotificationStatus] =
    useState<NotificationsStatus>(NOTIFICATION_STATUS.ALL);
  const [notificationGroup, setNotificationGroup] = useState<NotificationGroup>(
    NOTIFICATION_GROUP.FYA,
  );

  // Ref to maintain a stable reference to the current notifications array
  // Used in callbacks to access the latest notifications without causing re-renders
  const notificationsRef = useRef<NotificationType[]>([]);

  const { getIsLoading, updateIsLoading, updateIsLoadingIds } =
    useLoadingState();

  const { getAction } = useAction();

  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    params: { sessionId },
  } = useQueryParams();

  const dataRoomId = useMemo(() => {
    if (!pathname || !pathname.includes(DATA_ROOM)) return;

    const pathnameParts = pathname?.split('/');

    return pathnameParts[pathnameParts.indexOf(DATA_ROOM) + 1];
  }, [pathname]);

  const {
    currentPage,
    items: notifications,
    setItems: setNotifications,
    loading: fetchingNotifications,
    onContainerScrolled: onNotificationsScroll,
  } = useInfinitePagination<NotificationType>({
    condition: isLoggedIn,
    makeRequest: async (currentPage: number) => {
      return api.notifications
        .getNotifications({
          $page: currentPage.toString(),
          $limit: `${NOTIFICATIONS_LIMIT}`,
          $sort: '-createdAt',
          $relations:
            '["sender", "listing", "dataRoomQuestion", "dealRoom", "lead", "firstParticipant", "secondParticipant", "shareableLink"]',
          ...(!!dataRoomId && { dataRoomId }),
          group: notificationGroup,
          ...(getIsNotificationUnread(notificationStatus) && {
            isRead: false,
          }),
        })
        .then((res) => res.data);
    },
    resetDeps: [notificationStatus, dataRoomId, notificationGroup],
    dependencies: [notificationStatus, dataRoomId, notificationGroup],
  });

  const numberOfPlaceholders = useMemo(
    () =>
      NOTIFICATIONS_NUMBER_OF_PLACEHOLDERS[
        currentPage > 1 ? 'PAGINATED_LOADING_COUNT' : 'INITIAL_LOADING_COUNT'
      ],
    [currentPage],
  );

  const getCtaText = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_TRANSLATIONS[n?.typeCode] || 'review'),
    [t],
  );

  const getCtaPrimary = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_PRIMARY_TRANSLATIONS[n?.typeCode] || ''),
    [t],
  );

  const getCtaSecondary = useCallback(
    (n: NotificationType): string =>
      t(NOTIFICATIONS_CTA_SECONDARY_TRANSLATIONS[n?.typeCode] || ''),
    [t],
  );

  const getCtaIcon = useCallback((n: NotificationType) => {
    const Icon = NOTIFICATIONS_CTA_ICON[n?.typeCode] || ArrowrightIcon;

    return <Icon />;
  }, []);

  const getContent = useCallback(
    (notification: NotificationType) => {
      if (!notification) return '';

      const {
        typeCode,
        content: {
          sender,
          listing,
          numberOfEnabledDocuments,
          dataRoomQuestion,
          dataRoomQuestionSubject,
          dealRoom,
          fileNames,
          firstParticipant,
          secondParticipant,
          shareableLink,
        },
      } = notification || {};

      return t(NOTIFICATIONS_TRANSLATIONS[typeCode], {
        listingTitle: utils.getShortenedString(
          listing?.listingTitle ||
            dealRoom?.portfolioName ||
            dealRoom?.name ||
            listing?.dataRoomName,
        ),
        username: getUserFullNameOrEmail(sender),
        dataRoomName: dealRoom?.name || listing?.dataRoomName,
        count: (fileNames || []).length,
        documentsNames: getFileNames(fileNames),
        nudgesFileString: getNudgesString(fileNames),
        questionSubject: dataRoomQuestion?.subject || dataRoomQuestionSubject,
        documentName: dataRoomQuestion?.dataRoomNode?.name
          ? `for "${dataRoomQuestion?.dataRoomNode?.name}"`
          : '',
        numOfDocsMessage: t('numberOfDocsMessage', {
          count: numberOfEnabledDocuments || 0,
        }),
        dealName: dealRoom?.name,
        firstParticipantUsername: getUserFullNameOrEmail(firstParticipant),
        secondParticipantUsername: getUserFullNameOrEmail(secondParticipant),
        secureLinkName: shareableLink?.name,
      });
    },
    [t],
  );

  const redirectionPath = useCallback(
    ({
      typeCode,
      userType,
      content: {
        listing,
        sessionId,
        envelopeTemplateUrl,
        dataRoomQuestion,
        leadId,
        dataRoomNode,
        shareableLink,
      },
    }: NotificationType) =>
      getNotificationRedirectionPath({
        typeCode,
        listing,
        sessionId,
        envelopeTemplateUrl,
        leadId,
        type: userType,
        question: dataRoomQuestion,
        dataRoomNodeId: dataRoomNode?._id,
        shareableLink,
      }),
    [],
  );

  const getDynamicRedirectionPath = useCallback(
    (notif: NotificationType) => getNotificationRedirectionDynamicPath(notif),
    [],
  );

  const readAllNotifications = useCallback(async () => {
    try {
      // read all notification and update unread count
      updateIsLoading(true);

      await api.notifications.readAllNotifications();
      setNotificationsCount((oldCount) => ({ ...oldCount, unread: 0 }));

      setNotifications((oldNotifications) =>
        oldNotifications.map((notif) => {
          return {
            ...notif,
            isRead: true,
          };
        }),
      );
    } catch (e) {
      sentryError(e);
      popServerError(e);
    } finally {
      updateIsLoading(false);
    }
  }, [popServerError, setNotifications, updateIsLoading]);

  const deleteNotification = useCallback(
    async (notification: NotificationType) => {
      try {
        updateIsLoadingIds(notification._id);

        const { id: notifId, isRead } = notification;
        await api.notifications.deleteNotification(notifId);

        setNotificationsCount((oldCount) => ({
          ...oldCount,
          all: oldCount.all - 1,
          unread: !isRead ? oldCount.unread - 1 : oldCount.unread,
        }));

        setNotifications((oldNotifications) =>
          oldNotifications.filter((notif) => notif.id !== notifId),
        );
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        updateIsLoadingIds(notification._id, false);
      }
    },
    [popServerError, setNotifications, updateIsLoadingIds],
  );

  const onDeleteNotification = useCallback(
    (notification: NotificationType) => {
      deleteNotification(notification);
    },
    [deleteNotification],
  );

  const updateIsRead = useCallback(
    (notifId: string, isRead: boolean) => {
      setNotificationsCount((old) => ({
        ...old,
        unread: isRead ? (old.unread > 0 ? old.unread - 1 : 0) : old.unread + 1,
      }));

      setNotifications((oldNotifications) =>
        oldNotifications.map((notif) => {
          if (notif._id !== notifId) return notif;

          return {
            ...notif,
            isRead,
          };
        }),
      );
    },
    [setNotifications],
  );

  const readNotification = useCallback(
    async (notifId: string) => {
      const resolvedNotification = notificationsRef.current.find(
        ({ _id }) => _id === notifId,
      );

      const { isRead, typeCode } = resolvedNotification || {};

      try {
        // read notification by id and update values for
        // count of unread notifications
        updateIsLoadingIds(notifId);

        if (isRead) return;

        if (resolvedNotification) {
          mixpanelService.notificationViewedInitiated(
            getMixpanelProps({
              notificationCode: typeCode,
              notificationText: getContent(resolvedNotification),
            }) as NotificationViewedInitiatedEventProps,
          );
        }

        await api.notifications.readNotification(notifId);

        updateIsRead(notifId, true);
      } catch (e) {
        sentryError(e);
      } finally {
        updateIsLoadingIds(notifId, false);
      }
    },
    [getContent, getMixpanelProps, updateIsLoadingIds, updateIsRead],
  );

  const unreadNotification = useCallback(
    async (notifId: string) => {
      const resolvedNotification = notificationsRef.current.find(
        ({ _id }) => _id === notifId,
      );

      const { isRead } = resolvedNotification || {};

      try {
        // unread notification by id and update values for
        // count of unread notifications
        updateIsLoadingIds(notifId);

        if (!isRead) return;

        await api.notifications.unreadNotification(notifId);

        updateIsRead(notifId, false);
      } catch (e) {
        sentryError(e);
      } finally {
        updateIsLoadingIds(notifId, false);
      }
    },
    [updateIsLoadingIds, updateIsRead],
  );

  const onReadNotification = useCallback(
    (notifId: string, keepDrawerOpen = false) => {
      readNotification(notifId);
      setIsDrawerOpen(keepDrawerOpen);
    },
    [readNotification],
  );

  const onUnreadNotification = useCallback(
    (notifId: string, keepDrawerOpen = true) => {
      unreadNotification(notifId);
      setIsDrawerOpen(keepDrawerOpen);
    },
    [unreadNotification],
  );

  const onDynamicPath = useCallback(
    (notification: NotificationType) =>
      getNotificationRedirectionDynamicPath(notification),
    [],
  );

  const onStaticPath = useCallback((notification: NotificationType) => {
    const {
      typeCode,
      userType,
      content: {
        listing,
        sessionId,
        envelopeTemplateUrl,
        dataRoomQuestion,
        leadId,
        dataRoomNode,
        shareableLink,
      },
    } = notification;

    const pathname = getNotificationRedirectionPath({
      typeCode,
      listing,
      sessionId,
      envelopeTemplateUrl,
      leadId,
      type: userType,
      question: dataRoomQuestion,
      dataRoomNodeId: dataRoomNode?._id,
      shareableLink,
    });
    const isExternalPathname = !pathname || pathname.startsWith('http');

    if (isExternalPathname) return;

    return pathname;
  }, []);

  const onCtaClick = useCallback(
    async (notification: NotificationType) => {
      const staticPath = onStaticPath(notification);

      const action = getAction(notification);

      if (action) {
        action({ notification });
        return;
      }

      if (staticPath) {
        navigate(staticPath);
        return;
      }

      try {
        const externalUrl = await onDynamicPath(notification)();

        if (externalUrl) {
          window.location.href = externalUrl;
        }
      } catch (e) {
        popServerError(e);
        throw e;
      }
    },
    [getAction, navigate, onDynamicPath, onStaticPath, popServerError],
  );

  useEffect(() => {
    // load inital count for unread and all notifications
    (async () => {
      try {
        if (!isLoggedIn) return;
        updateIsLoading(true);

        const {
          data: {
            unreadNotificationsCount: unread,
            allNotificationsCount: all,
          },
        } = await api.notifications.getUnreadNotifications({
          ...(!!dataRoomId && { dataRoomId }),
        });

        setNotificationsCount({ all, unread });
      } catch (e) {
        popServerError(e);
      } finally {
        updateIsLoading(false);
      }
    })();
  }, [dataRoomId, isLoggedIn, popServerError, updateIsLoading]);

  const notificationCreated = useCallback(
    (notification: NotificationType) => {
      if (!notification.justNotify) {
        setNotifications((oldNotifications) => [
          notification,
          ...oldNotifications,
        ]);
        setNotificationsCount((oldCount) => ({
          all: oldCount.all + 1,
          unread: oldCount.unread + 1,
        }));
      }

      const dontShowMessageNotification =
        (sessionId && sessionId === notification.content.sessionId) ||
        (notification.content.sessionId &&
          pathname.includes(notification.content.sessionId));

      const dontNotify = DONT_NOTIFY.includes(notification.typeCode);

      const isNotRelatedToSameDR =
        !!dataRoomId && dataRoomId !== notification.dataRoomId;

      if (
        !dontNotify &&
        !dontShowMessageNotification &&
        !isNotRelatedToSameDR
      ) {
        notify({
          notification,
          ctaText: getCtaText(notification),
          ctaIcon: getCtaIcon(notification),
          contentText: getContent(notification),
          dissappearAfter: DEFAULT_ANIMATION_TIME_MS,
          onCtaClick: () => onCtaClick(notification),
          onClick: () => readNotification(notification._id),
        });
      }
    },
    [
      dataRoomId,
      pathname,
      sessionId,
      getContent,
      getCtaIcon,
      getCtaText,
      onCtaClick,
      readNotification,
      setNotifications,
    ],
  );

  useSocketEvent(LISTEN_EVENTS.NOTIFICATION_CREATED, notificationCreated);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  return (
    <NotificationsContext.Provider
      value={{
        fetchingNotifications,
        numberOfPlaceholders,
        isDrawerOpen,
        notificationsCount,
        notifications,
        notificationStatus,
        notificationGroup,
        setNotificationStatus,
        setNotificationGroup,
        deleteNotification,
        setIsDrawerOpen,
        setNotifications,
        onNotificationsScroll,
        readAllNotifications,
        redirectionPath,
        getDynamicRedirectionPath,
        onDeleteNotification,
        onReadNotification,
        onUnreadNotification,
        getCtaText,
        getCtaPrimary,
        getCtaSecondary,
        getCtaIcon,
        getContent,
        getIsLoadingNotification: getIsLoading,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default Notifications;
